import { FileDownload, FolderZip, Info, TableRows } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Input,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { useCallback, useEffect } from "react";
import { CSVLink } from "react-csv";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { useImportCandidateCSVMutation } from "../../Queries/ImportQueries";
import { useDebouncedHandler } from "../../State/Hooks/useDebouncedHandler";
import { useImportStatusStore } from "../../State/Stores/ImportStatusStore";
import Modal from "../Modal/Modal";
import InlineCode from "../Typography/InlineCode";

const MaxCsvFileSize = 10 * 1024 * 1024; // 10MB
const MaxZipFileSize = 150 * 1024 * 1024; // 150MB
const StandardHeaders = ["firstname", "lastname", "email", "phone", "company", "title", "location"];
const OptionalHeaders = ["tags", "areaOfInterest"];

const MuiCSVLink = styled(CSVLink)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  textDecoration: "none",
  padding: "0 !important",
}));

export default function CSVImportModal({ groupId }: { groupId?: string }) {
  const { isUploadError, isUploadSuccess, showImportModal, setGroupId, setShowImportModal, reset } =
    useImportStatusStore(
      useShallow((state) => ({
        groupId: state.groupId,
        showImportModal: state.showImportModal,
        isUploadError: state.isUploadError,
        isUploadSuccess: state.isUploadSuccess,
        setGroupId: state.setGroupId,
        setShowImportModal: state.setShowImportModal,
        reset: state.reset,
      })),
    );

  let bodyContent = <UploadBody />;
  let footerContent = <UploadFooter />;

  if (isUploadError) {
    bodyContent = <UploadErrorBody />;
    footerContent = <UploadErrorFooter />;
  }
  if (isUploadSuccess) {
    bodyContent = <UploadSuccessBody />;
    footerContent = <UploadSuccessFooter />;
  }

  const handleCloseModal = () => {
    setShowImportModal(false);
    setTimeout(() => {
      reset();
    }, 300);
  };

  useEffect(() => {
    if (groupId) {
      setGroupId(groupId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  return (
    <Modal
      title="Import Candidates"
      dataTestId="import-csv"
      isOpen={showImportModal}
      handleCloseModal={handleCloseModal}
      content={bodyContent}
      footerContent={footerContent}
    />
  );
}

export const UploadBody = () => {
  const { isLoading } = useImportCandidateCSVMutation();
  const {
    fileName,
    isInputError,
    inputErrorMessage,
    includeZipFile,
    isZipInputError,
    zipFileName,
    zipInputErrorMessage,
    resetZip,
    setInputError,
    setFile,
    setZipFile,
    setIncludeZipFile,
    setZipInputError,
  } = useImportStatusStore(
    useShallow((state) => ({
      fileName: state.fileName,
      isInputError: state.isInputError,
      inputErrorMessage: state.inputErrorMessage,
      isZipInputError: state.isZipInputError,
      includeZipFile: state.includeZipFile,
      zipFileName: state.zipFileName,
      zipInputErrorMessage: state.zipInputErrorMessage,
      resetZip: state.resetZip,
      setInputError: state.setInputError,
      setFile: state.setFile,
      setZipFile: state.setZipFile,
      setIncludeZipFile: state.setIncludeZipFile,
      setZipInputError: state.setZipInputError,
    })),
  );

  const handleUploadFile: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (!e.target.files) {
      return;
    }
    setInputError(false, "");

    const incomingFile = e.target.files[0];
    const incomingFileName = incomingFile.name;
    const isFileCSV = incomingFileName.substring(incomingFileName.length - 4) === ".csv";
    const isFileSizeValid = incomingFile.size <= MaxCsvFileSize;

    if (!isFileCSV) {
      setInputError(true, "File must be a CSV");
      setFile(null, incomingFileName);
      return;
    }
    if (!isFileSizeValid) {
      setInputError(true, "File size cannot exceed 10MB");
      setFile(null, incomingFileName);
      return;
    }

    setFile(incomingFile, incomingFileName);
  };

  const handleToggleZipUpload = () => {
    const isActive = includeZipFile;
    setIncludeZipFile(!includeZipFile);
    if (isActive) {
      // When disabling attachments, clear zip state and errors
      resetZip();
      setZipInputError(false, "");
    }
  };

  const handleUploadZipFile: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (!e.target.files) {
      return;
    }
    setZipInputError(false, "");

    const incomingFile = e.target.files[0];
    if (!incomingFile) {
      setZipFile(null, "");
      setZipInputError(false, "");
      return;
    }

    const incomingFileName = incomingFile.name;
    const isFileZip = incomingFileName.substring(incomingFileName.length - 4) === ".zip";
    const isFileSizeValid = incomingFile.size <= MaxZipFileSize;

    if (!isFileZip) {
      setZipFile(null, incomingFileName);
      setZipInputError(true, "File must be a ZIP");
      return;
    }
    if (!isFileSizeValid) {
      setZipInputError(true, "File size cannot exceed 150MB");
      setZipFile(null, incomingFileName);
      return;
    }

    setZipFile(incomingFile, incomingFileName);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box
        id="form-description"
        sx={{
          "& .MuiTypography-root": {
            fontWeight: "normal",
          },
        }}
      >
        <Typography paragraph sx={{ mb: 0 }}>
          Easily import your candidates' data using our CSV template. Ensure your data aligns with our
          accepted headers for a seamless import experience.
        </Typography>
        <Tooltip
          arrow
          placement="bottom-start"
          title={"Attach a ZIP file containing candidate resumes as TXT, DOC, DOCX or PDF."}
        >
          <Stack direction="row" alignItems="center" spacing={2} width="auto" sx={{ display: "inline-flex" }}>
            <FormControlLabel
              data-test="include-zip-file-checkbox"
              control={
                <>
                  <Checkbox checked={includeZipFile} onChange={handleToggleZipUpload} />
                  <Info fontSize="small" sx={{ ml: 1, order: 3 }} />
                </>
              }
              label="Include Attachments"
            />
          </Stack>
        </Tooltip>
        <Alert data-test="upload-helper-text" severity="info" sx={{ mb: 2 }}>
          <Typography paragraph gutterBottom data-test="required-headers">
            <strong>Required:</strong> {renderWhiteBackgroundCode("firstname")},{" "}
            {renderWhiteBackgroundCode("lastname")},{" "}
            {includeZipFile && <>{renderWhiteBackgroundCode("attachments")}, </>}
            either {renderWhiteBackgroundCode("email")} or {renderWhiteBackgroundCode("phone")}
          </Typography>
          <Typography paragraph gutterBottom data-test="optional-headers">
            <strong>Optional:</strong> {renderWhiteBackgroundCode("company")},{" "}
            {renderWhiteBackgroundCode("title")}, {renderWhiteBackgroundCode("location")},{" "}
            {renderWhiteBackgroundCode("tags")}, {renderWhiteBackgroundCode("areaOfInterest")}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography paragraph gutterBottom>
            Ensure location entries with commas are in quotes:{" "}
            <strong>"Street Address, City, State, Zip"</strong>
          </Typography>
          <Typography paragraph gutterBottom>
            Use <strong>; or |</strong> to separate multiple values in {renderWhiteBackgroundCode("tags")},{" "}
            {renderWhiteBackgroundCode("areaOfInterest")}
            {includeZipFile && <>, or {renderWhiteBackgroundCode("attachments")}</>} columns
          </Typography>
          <Typography paragraph gutterBottom>
            CSV files cannot exceed 10MB in size
          </Typography>
          <Collapse data-test="upload-zip-helper-text" in={includeZipFile}>
            <Divider sx={{ mb: 2 }} />
            <Typography paragraph gutterBottom>
              When uploading a zip file, {renderWhiteBackgroundCode("attachments")} is required as a column
              header in the CSV
            </Typography>
            <Typography paragraph gutterBottom>
              Each row with an attachment should have a corresponding file in the zip archive. Multiple
              attachments should be separated by a | character like <strong>file1.pdf|file2.docx</strong>
            </Typography>
            <Typography paragraph gutterBottom>
              Zip files cannot exceed 150MB in size
            </Typography>
          </Collapse>
          <Box
            sx={{
              display: "flex",
              "& a": {
                float: "none !important",
              },
            }}
          >
            <MuiCSVLink
              headers={
                includeZipFile
                  ? [...StandardHeaders, ...OptionalHeaders, "attachments"]
                  : [...StandardHeaders, ...OptionalHeaders]
              }
              data={""}
              style={{ padding: "1em", float: "left" }}
              filename={"import-candidates_template"}
            >
              <Button
                color="info"
                variant="outlined"
                sx={(theme) => ({ display: "flex", gap: theme.spacing(1) })}
              >
                <FileDownload color="info" /> Download Our CSV Template
              </Button>
            </MuiCSVLink>
          </Box>
        </Alert>
      </Box>
      <Box>
        <FormLabel
          htmlFor="contained-button-file"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box sx={{ width: "auto" }}>
            <Input
              aria-describedby="form-description"
              id="contained-button-file"
              type="file"
              required={true}
              style={{ display: "none" }}
              onChange={handleUploadFile}
            />
            <Button
              aria-label="Choose a CSV file"
              variant="outlined"
              color={isInputError ? "error" : "primary"}
              component="span"
              style={{ marginTop: "10px", whiteSpace: "nowrap" }}
              disabled={isLoading}
              startIcon={<TableRows />}
            >
              Select CSV File
            </Button>
          </Box>

          <TextField
            placeholder="File Name"
            label="File Name"
            variant="standard"
            fullWidth
            required
            disabled
            error={isInputError}
            value={fileName}
            sx={{
              pointerEvents: "none",
              "& .Mui-disabled": { color: "black", WebkitTextFillColor: "black" },
            }}
          />
        </FormLabel>
        {isInputError && (
          <FormHelperText data-test="csv-filetype-error-message" sx={{ color: "error.main" }}>
            {inputErrorMessage}
          </FormHelperText>
        )}
      </Box>
      <Collapse data-test="zip-file-inputs" data-active={includeZipFile} in={includeZipFile}>
        <Box>
          <FormLabel
            htmlFor="contained-button-file-zip"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box sx={{ width: "auto" }}>
              <Input
                aria-describedby="form-description"
                id="contained-button-file-zip"
                type="file"
                required={true}
                style={{ display: "none" }}
                onChange={handleUploadZipFile}
              />
              <Button
                aria-label="Choose a ZIP file"
                variant="outlined"
                color={isInputError ? "error" : "primary"}
                component="span"
                style={{ marginTop: "10px", whiteSpace: "nowrap" }}
                disabled={isLoading}
                startIcon={<FolderZip />}
              >
                Select Zip File
              </Button>
            </Box>

            <TextField
              placeholder="Zip File Name"
              label="Zip File Name"
              variant="standard"
              fullWidth
              required
              disabled
              error={isZipInputError}
              value={zipFileName}
              sx={{
                pointerEvents: "none",
                "& .Mui-disabled": { color: "black", WebkitTextFillColor: "black" },
              }}
            />
          </FormLabel>
          {isZipInputError && (
            <FormHelperText data-test="zip-filetype-error-message" sx={{ color: "error.main" }}>
              {zipInputErrorMessage}
            </FormHelperText>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const renderWhiteBackgroundCode = (code: string) => (
  <InlineCode sx={{ backgroundColor: "white" }}>{code}</InlineCode>
);

export const UploadSuccessBody = () => {
  const { groupId, statusId, setShowImportModal, reset } = useImportStatusStore(
    useShallow((state) => ({
      groupId: state.groupId,
      statusId: state.statusId,
      setShowImportModal: state.setShowImportModal,
      reset: state.reset,
    })),
  );
  const navigate = useNavigate();
  const linkUrl = groupId ? `/candidategroups/${groupId}/imports` : "/candidates/imports";

  const handleClickDetails = () => {
    setShowImportModal(false);
    reset();
    navigate(`${linkUrl}?importId=${statusId}`);
  };

  return (
    <Stack id="csv-upload-success" alignItems="center">
      <Typography color="success" sx={{ fontWeight: "bold" }}>
        Upload Successful
      </Typography>
      <Typography paragraph>Your import is processing</Typography>
      <Button color="primary" variant="outlined" onClick={handleClickDetails} data-test="import-details-link">
        View Import Details
      </Button>
    </Stack>
  );
};

export const UploadSuccessFooter = () => {
  const { setShowImportModal, reset } = useImportStatusStore(
    useShallow((state) => ({
      setShowImportModal: state.setShowImportModal,
      reset: state.reset,
    })),
  );

  function handleCloseModal() {
    setShowImportModal(false);
    setTimeout(() => {
      reset();
    }, 300);
  }

  return (
    <Button
      aria-label="Close Import Modal"
      type="submit"
      variant="contained"
      color="primary"
      onClick={handleCloseModal}
      data-test="csv-close-button"
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "center",
      }}
    >
      Close
    </Button>
  );
};

export const UploadFooter = () => {
  const { mutateAsync: submitImportCSV, isLoading: isUploading } = useImportCandidateCSVMutation();
  const {
    file,
    zipFile,
    groupId,
    includeZipFile,
    isInputError,
    isZipInputError,
    setInputError,
    setZipInputError,
  } = useImportStatusStore(
    useShallow((state) => ({
      file: state.file,
      zipFile: state.zipFile,
      groupId: state.groupId,
      includeZipFile: state.includeZipFile,
      isInputError: state.isInputError,
      isZipInputError: state.isZipInputError,
      setInputError: state.setInputError,
      setZipInputError: state.setZipInputError,
    })),
  );

  const handleSubmitCSV = useCallback(
    async function submitFile() {
      const missingCsv = file === null;
      const missingZip = includeZipFile && zipFile === null;
      const disabled = missingCsv || isUploading || missingZip;
      if (!disabled) {
        const fileData = new FormData();
        fileData.append("file", file);
        if (zipFile) {
          fileData.append("zipFile", zipFile);
        }
        await submitImportCSV({
          file: fileData,
          groupId: groupId,
        });
      } else {
        if (missingCsv) {
          setInputError(true, "Please select a valid CSV file");
        }
        if (missingZip) {
          setZipInputError(true, "Please select a valid ZIP file");
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [file, zipFile, includeZipFile, submitImportCSV, isUploading],
  );

  const handleSubmit = useDebouncedHandler(handleSubmitCSV, 300);

  return (
    <Stack
      direction={"row"}
      spacing={1}
      sx={{ padding: 0 }}
      justifyContent={"center"}
      alignContent={"center"}
      alignItems={"center"}
    >
      <Typography variant="caption" sx={{ lineHeight: 1.5, fontSize: 12, padding: "0 10px 0 10px" }}>
        By importing these candidates, you confirm that you have obtained the necessary consent to contact
        them as required by applicable laws and regulations.
      </Typography>
      <Button
        aria-label="Submit Import"
        type="submit"
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        data-test="csv-submit-button"
        disabled={isInputError || isZipInputError || isUploading}
      >
        Submit
        {isUploading && (
          <Box sx={{ display: "flex" }}>
            <CircularProgress size={20} color="inherit" />
          </Box>
        )}
      </Button>
    </Stack>
  );
};

export const UploadErrorBody = () => {
  const includeZipFile = useImportStatusStore((state) => state.includeZipFile);
  const uploadErrorMessage = useImportStatusStore((state) => state.uploadErrorMessage);
  return uploadErrorMessage ? (
    <Stack id="csv-upload-error" alignItems="center">
      <Typography color="error" sx={{ fontWeight: "bold" }}>
        Upload Error
      </Typography>
      <Typography paragraph>{uploadErrorMessage}</Typography>
    </Stack>
  ) : (
    <Box id="csv-upload-error">
      <Typography paragraph gutterBottom sx={{ fontWeight: "bold" }}>
        An error occurred while importing candidates. Ensure all required CSV headers are present in the file.
        Required CSV headers: <span style={{ color: "red" }}>firstname, lastname</span>
        {includeZipFile && <span style={{ color: "red" }}>, attachments</span>}, and at least one of{" "}
        <span style={{ color: "red" }}>email</span> or <span style={{ color: "red" }}>phone</span>.
      </Typography>
    </Box>
  );
};

export const UploadErrorFooter = () => {
  const { reset, setShowImportModal } = useImportStatusStore(
    useShallow((state) => ({
      reset: state.reset,
      setShowImportModal: state.setShowImportModal,
    })),
  );

  const handleModalClose = () => {
    setShowImportModal(false);
    setTimeout(() => {
      reset();
    }, 300);
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}>
      <Button
        type="submit"
        variant="outlined"
        color="primary"
        onClick={handleModalClose}
        data-test="csv-cancel-button"
      >
        Cancel
      </Button>
      <Button type="submit" variant="contained" color="primary" onClick={reset} data-test="csv-retry-button">
        Retry
      </Button>
    </Box>
  );
};

export const UploadErrorNotification = () => (
  <>
    <Typography>An error occurred while uploading your file. Please try again or </Typography>
    <Link color="inherit" component={RouterLink} to={`/candidates/imports`}>
      View Imports
    </Link>
  </>
);
